/**
 * Wagmi & RainbowKit Configuration
 *
 * Modern Web3 configuration using:
 * - Wagmi v2 with Viem
 * - RainbowKit v2
 * - React Query v5
 *
 * @module config/wagmi
 */

import { getDefaultConfig } from "@rainbow-me/rainbowkit";
import {
  metaMaskWallet,
  walletConnectWallet,
  coinbaseWallet,
  trustWallet,
  rabbyWallet,
  rainbowWallet,
  ledgerWallet,
} from "@rainbow-me/rainbowkit/wallets";
import { http, fallback } from "wagmi";
import {
  mainnet,
  polygon as polygonBase,
  arbitrum,
  optimism,
  bsc,
  sepolia,
  celo,
} from "wagmi/chains";
import { defineChain, parseGwei } from "viem";

/**
 * Custom Polygon chain with a static `defaultPriorityFee`.
 * Some public Polygon RPCs do not implement `eth_maxPriorityFeePerGas`, which
 * causes MetaMask / viem to log an RPC error during fee estimation. Providing a
 * static priority fee (30 gwei – Polygon's required minimum) lets viem skip
 * that RPC call entirely and populate the transaction field itself.
 */
const polygon = defineChain({
  ...polygonBase,
  // Polygon rebranded MATIC → POL in September 2024
  nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
  fees: {
    defaultPriorityFee: parseGwei("30"),
  },
});

// WalletConnect Project ID
const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || "bdcff7fa6537b9c45580c17656907b83";

// App metadata for wallet connections
const appInfo = {
  appName: "FunDeSci",
  projectId,
};

// Alchemy API key for premium RPC access (set via environment variable)
const alchemyApiKey = process.env.REACT_APP_ALCHEMY_API_KEY;

// Ankr API key for backup RPC access (set via environment variable)
const ankrApiKey = process.env.REACT_APP_ANKR_API_KEY;

// Optional full Ethereum mainnet RPC URL for Infura, QuickNode, self-hosted, etc.
const ethereumRpcUrl = process.env.REACT_APP_ETHEREUM_RPC_URL;

/**
 * Build transport array with optional API-based RPCs
 * Only includes Alchemy/Ankr endpoints if API keys are configured
 */
const buildTransport = (alchemyUrl, ankrUrl, publicUrls, customUrls = []) => {
  const transports = [];
  customUrls.filter(Boolean).forEach((url) => transports.push(http(url)));
  if (alchemyApiKey && alchemyUrl) transports.push(http(alchemyUrl));
  if (ankrApiKey && ankrUrl) transports.push(http(ankrUrl));
  transports.push(...publicUrls.map(url => http(url)));
  return fallback(transports);
};

/**
 * Wagmi configuration with RainbowKit defaults
 * Uses getDefaultConfig for simplified setup with best practices
 * Custom transports with fallback RPCs for better reliability
 *
 * Wallets are listed explicitly instead of relying on the generic
 * "Injected" connector. The generic connector maps to window.ethereum,
 * which other extensions (e.g. OKX Wallet) may override. Named wallet
 * connectors target each wallet by its EIP-6963 RDNS identifier, so
 * clicking MetaMask always opens MetaMask regardless of which extension
 * grabbed window.ethereum.
 *
 * RPC Priority Order:
 * 1. Alchemy (primary - has API credits) - only if REACT_APP_ALCHEMY_API_KEY is set
 * 2. Ankr with API key (backup) - only if REACT_APP_ANKR_API_KEY is set
 * 3. Public fallback RPCs
 */
export const wagmiConfig = getDefaultConfig({
  appName: "FunDeSci",
  projectId,
  chains: [
    mainnet,
    polygon,
    arbitrum,
    optimism,
    bsc,
    sepolia,
    celo,
  ],
  // Explicit wallet list — do NOT include injectedWallet here because it uses
  // window.ethereum directly and would show whichever wallet hijacked it (OKX, etc.)
  wallets: [
    {
      groupName: "Recommended",
      wallets: [metaMaskWallet, walletConnectWallet, coinbaseWallet],
    },
    {
      groupName: "More",
      wallets: [trustWallet, rabbyWallet, rainbowWallet, ledgerWallet],
    },
  ],
  transports: {
    // Ethereum Mainnet
    [mainnet.id]: buildTransport(
      `https://eth-mainnet.g.alchemy.com/v2/${alchemyApiKey}`,
      `https://rpc.ankr.com/eth/${ankrApiKey}`,
      ["https://eth.drpc.org", "https://rpc.ankr.com/eth"],
      [ethereumRpcUrl]
    ),
    // Polygon
    [polygon.id]: buildTransport(
      `https://polygon-mainnet.g.alchemy.com/v2/${alchemyApiKey}`,
      `https://rpc.ankr.com/polygon/${ankrApiKey}`,
      ["https://polygon.drpc.org"]
    ),
    // Arbitrum
    [arbitrum.id]: buildTransport(
      `https://arb-mainnet.g.alchemy.com/v2/${alchemyApiKey}`,
      `https://rpc.ankr.com/arbitrum/${ankrApiKey}`,
      ["https://arbitrum.drpc.org", "https://arb1.arbitrum.io/rpc"]
    ),
    // Optimism
    [optimism.id]: buildTransport(
      `https://opt-mainnet.g.alchemy.com/v2/${alchemyApiKey}`,
      null, // Ankr doesn't have Optimism with API key
      ["https://optimism.drpc.org", "https://mainnet.optimism.io"]
    ),
    // BSC - Alchemy doesn't support BSC, use Ankr first
    [bsc.id]: buildTransport(
      null, // No Alchemy for BSC
      `https://rpc.ankr.com/bsc/${ankrApiKey}`,
      ["https://bsc.drpc.org", "https://bsc-dataseed.binance.org"]
    ),
    // Sepolia Testnet
    [sepolia.id]: buildTransport(
      `https://eth-sepolia.g.alchemy.com/v2/${alchemyApiKey}`,
      `https://rpc.ankr.com/eth_sepolia/${ankrApiKey}`,
      ["https://ethereum-sepolia-rpc.publicnode.com", "https://sepolia.drpc.org", "https://rpc2.sepolia.org"]
    ),
    // Celo - Alchemy and Ankr don't support Celo, use public nodes
    [celo.id]: buildTransport(
      null,
      null,
      ["https://forno.celo.org", "https://rpc.ankr.com/celo", "https://celo.drpc.org"]
    ),
  },
  ssr: false, // Client-side only
});

// Export supported chains for use elsewhere
// Note: `polygon` here is the customised chain with the fee override.
export const supportedChains = [
  mainnet,
  polygon,
  arbitrum,
  optimism,
  bsc,
  sepolia,
  celo,
];

// Export chain IDs for convenience
export const chainIds = {
  ETHEREUM: mainnet.id,
  POLYGON: polygon.id,
  ARBITRUM: arbitrum.id,
  OPTIMISM: optimism.id,
  BSC: bsc.id,
  SEPOLIA: sepolia.id,
  CELO: celo.id,
};

export { appInfo, projectId };
